/* i18n bootstrap.

   Only the active locale is fetched. The Arabic bundle is a few kilobytes, but
   the principle matters more than the bytes: nothing a visitor cannot read
   should be on the critical path, and the same lazy loader will hold when the
   third and fourth locales land.

   Direction is applied to <html> here rather than by a React effect, because
   the boot screen in index.html paints before React exists and a left-aligned
   flash before an Arabic page is exactly the kind of seam that makes a web app
   feel like a web app. */

import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './en'

export const LOCALES = ['en', 'ar'] as const
export type Locale = (typeof LOCALES)[number]

export const RTL_LOCALES = new Set<Locale>(['ar'])

const STORE_KEY = 'hexclean:v1:locale'

/** Each language written in itself — an endonym.
 *
 *  This is the one piece of copy in the app that must never be translated. A
 *  language picker exists for someone who cannot read the language currently
 *  on screen, so labelling the Arabic option "Arabic" hides it from exactly
 *  the person it is for. Every list that offers a language — Wikipedia's, the
 *  OS settings pane, a passport form — writes each one in its own script for
 *  this reason, and that is also why the labels carry `lang` and `dir` when
 *  rendered: the browser then reaches for the right font stack per option
 *  rather than rendering Arabic through a Latin face's fallback. */
export const LANGUAGES: readonly { code: Locale; name: string; dir: 'ltr' | 'rtl' }[] = [
  { code: 'en', name: 'English', dir: 'ltr' },
  { code: 'ar', name: 'العربية', dir: 'rtl' },
]

export function languageOf(locale: Locale) {
  return LANGUAGES.find((l) => l.code === locale) ?? LANGUAGES[0]!
}

export function isRtl(locale: Locale): boolean {
  return RTL_LOCALES.has(locale)
}

/** Stored choice → browser preference → English. */
export function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORE_KEY)
    if (stored && (LOCALES as readonly string[]).includes(stored)) return stored as Locale
  } catch {
    /* storage disabled — fall through to the browser's own preference */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2) : 'en'
  return (LOCALES as readonly string[]).includes(nav) ? (nav as Locale) : 'en'
}

/** `lang` and `dir` on the root element, and the head metadata that is not part
 *  of the React tree.
 *
 *  The description and the Open Graph pair are static in index.html, which is
 *  correct for a crawler — it reads the served HTML — but leaves a share sheet
 *  and a bookmark in English for a visitor reading Arabic. `document.title`
 *  itself is set per route in components/layout/DocumentTitle. */
export function applyDocumentLocale(locale: Locale): void {
  const root = document.documentElement
  root.lang = locale
  root.dir = isRtl(locale) ? 'rtl' : 'ltr'

  const description = i18next.t('title.description', { lng: locale, defaultValue: '' })
  if (description) {
    setMeta('name', 'description', description)
    setMeta('property', 'og:description', description)
  }
  setMeta('property', 'og:locale', locale === 'ar' ? 'ar_SA' : 'en_SA')
}

function setMeta(attr: 'name' | 'property', key: string, value: string): void {
  const tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (tag) tag.content = value
}

async function loadBundle(locale: Locale) {
  if (locale === 'en') return en
  const mod = await import('./ar')
  return mod.default
}

export async function initI18n(): Promise<Locale> {
  const locale = detectLocale()
  const resources = await loadBundle(locale)

  await i18next.use(initReactI18next).init({
    lng: locale,
    fallbackLng: 'en',
    /* English is always present as the fallback bundle so a key that has not
       been translated yet renders readable copy instead of `account.title`. */
    resources: locale === 'en' ? { en: { t: en } } : { [locale]: { t: resources }, en: { t: en } },
    ns: ['t'],
    defaultNS: 't',
    interpolation: { escapeValue: false },
    returnNull: false,
  })

  applyDocumentLocale(locale)
  return locale
}

/** Fetch a locale's bundle without changing anything on screen.
 *
 *  Split from the commit below so a caller animating the change can overlap
 *  the download with its own cover. Folded together, the document flips to the
 *  new direction the instant the bytes land — which on a warm cache is the
 *  frame after the press, well before any curtain has finished coming down,
 *  and the reflow the curtain exists to hide happens in plain view. */
export async function prepareLocale(locale: Locale): Promise<void> {
  if (!i18next.hasResourceBundle(locale, 't')) {
    i18next.addResourceBundle(locale, 't', await loadBundle(locale), true, true)
  }
}

/** Make a prepared locale the live one: persist it, switch i18next, and stamp
 *  the document. Everything here is visible, and all of it lands together. */
export async function commitLocale(locale: Locale): Promise<void> {
  try {
    localStorage.setItem(STORE_KEY, locale)
  } catch {
    /* a locale that does not survive a reload is still better than a crash */
  }
  await i18next.changeLanguage(locale)
  applyDocumentLocale(locale)
}

/** Both halves, for callers with nothing to synchronise against. */
export async function setLocale(locale: Locale): Promise<void> {
  await prepareLocale(locale)
  await commitLocale(locale)
}

export { i18next }
